/* ──────────────────────────────────────────────────────────────────────
 * ConversionGauge — semicircular needle gauge
 *
 * 180° arc from graphite (0%) to champagne (100%), 16px stroke. A
 * thin needle swings from 0 to the current value on mount with an
 * ease-out-back curve. Centre-bottom carries the value as an
 * oversized serif numeral + a tiny CONVERSION label.
 *
 * Below the gauge: three small stats (quotes sent · closed · avg deal)
 * in a row. Calculator-style monospace numbers + italic serif labels.
 * ──────────────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import { Box, Typography, Stack } from "@mui/material";

const CHAMPAGNE     = "#C9A961";
const CHAMPAGNE_HI  = "#E5C77E";
const GRAPHITE      = "#3a4150";
const INK           = "#0F172A";
const SLATE         = "#64748b";
const FONT_SERIF    = `'Fraunces', 'GT Sectra', Georgia, serif`;
const FONT_MONO     = `'JetBrains Mono', 'SF Mono', Menlo, monospace`;

interface Props {
  /** 0..100 — the conversion rate to display. */
  value: number;
  /** Mini stats below the gauge. */
  stats?: { label: string; value: string }[];
}

const VBOX_W = 240;
const VBOX_H = 140;
const CX = VBOX_W / 2;
const CY = 120;       // pivot — sits low so the arc fills the box
const R  = 92;        // gauge radius
const STROKE = 16;

/** Convert a 0..100 value into the (-180°…0°) sweep we need. */
function valToAngle(v: number): number {
  const clamped = Math.max(0, Math.min(100, v));
  return -180 + (clamped / 100) * 180;
}

function polar(angleDeg: number, radius: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CX + radius * Math.cos(rad), y: CY + radius * Math.sin(rad) };
}

function arcPath(startDeg: number, endDeg: number, radius: number): string {
  const start = polar(startDeg, radius);
  const end   = polar(endDeg, radius);
  const large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  const sweep = endDeg > startDeg ? 1 : 0;
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${radius} ${radius} 0 ${large} ${sweep} ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}

export default function ConversionGauge({ value, stats }: Props) {
  // Animate the needle from 0 to its target value with an ease-out-back
  // curve. We tween over 1000ms; rAF would be smoother but the CSS
  // transition is simpler and the start-from-zero on mount is enough
  // to read as a sweeping motion.
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    // Tiny delay so the initial paint shows 0 then sweeps.
    const id = window.setTimeout(() => setDisplayValue(value), 60);
    return () => clearTimeout(id);
  }, [value]);

  const angle = valToAngle(displayValue);
  const needleEnd = polar(angle, R - STROKE);

  return (
    <Box sx={{ textAlign: "center" }}>
      <Box component="svg" viewBox={`0 0 ${VBOX_W} ${VBOX_H}`} sx={{ width: "100%", maxWidth: 280, height: "auto", display: "block", mx: "auto" }}>
        <defs>
          <linearGradient id="gauge-arc" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor={GRAPHITE} />
            <stop offset="60%"  stopColor={CHAMPAGNE} />
            <stop offset="100%" stopColor={CHAMPAGNE_HI} />
          </linearGradient>
          <filter id="gauge-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" />
          </filter>
        </defs>

        {/* Track arc — faint ground */}
        <path
          d={arcPath(-180, 0, R)}
          fill="none"
          stroke="rgba(10,14,26,0.07)"
          strokeWidth={STROKE}
          strokeLinecap="round"
        />
        {/* Coloured arc — full gradient sweep */}
        <path
          d={arcPath(-180, 0, R)}
          fill="none"
          stroke="url(#gauge-arc)"
          strokeWidth={STROKE}
          strokeLinecap="round"
          filter="url(#gauge-glow)"
          opacity={0.85}
        />
        {/* Crisp arc on top */}
        <path
          d={arcPath(-180, 0, R)}
          fill="none"
          stroke="url(#gauge-arc)"
          strokeWidth={STROKE - 4}
          strokeLinecap="round"
          opacity={0.4}
        />

        {/* Major ticks at 0 / 25 / 50 / 75 / 100 — thin lines */}
        {[0, 25, 50, 75, 100].map((p) => {
          const a = valToAngle(p);
          const inner = polar(a, R - STROKE / 2 - 6);
          const outer = polar(a, R - STROKE / 2 - 2);
          return (
            <line
              key={p}
              x1={inner.x} y1={inner.y}
              x2={outer.x} y2={outer.y}
              stroke="rgba(255,255,255,0.85)"
              strokeWidth={1}
            />
          );
        })}

        {/* Needle — animates from -180° (the leftmost) on mount.
            Implementation: we render the needle at the current
            `angle` and rely on CSS transition on the `transform`
            of the SVG group to animate the swing. */}
        <g
          style={{
            transformOrigin: `${CX}px ${CY}px`,
            transform: `rotate(${angle + 180}deg)`,
            transition: "transform 1000ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        >
          <line
            x1={CX} y1={CY}
            x2={CX - (R - STROKE)} y2={CY}
            stroke={INK}
            strokeWidth={2.5}
            strokeLinecap="round"
          />
          <circle cx={CX} cy={CY} r={5} fill={INK} />
          <circle cx={CX} cy={CY} r={2.5} fill={CHAMPAGNE_HI} />
        </g>
      </Box>

      {/* Centre-bottom value */}
      <Box mt={-1.5}>
        <Typography sx={{
          fontFamily: FONT_SERIF,
          fontSize: 44, fontWeight: 300,
          color: INK, lineHeight: 0.95,
          letterSpacing: "-0.025em",
          fontVariantNumeric: "tabular-nums",
        }}>
          {Math.round(value)}<Box component="span" sx={{ fontSize: 22 }}>%</Box>
        </Typography>
        <Typography sx={{
          fontSize: 9, fontWeight: 600, color: SLATE,
          letterSpacing: "0.22em", textTransform: "uppercase",
          mt: 0.4,
        }}>
          Conversion
        </Typography>
      </Box>

      {/* Mini stats row */}
      {stats && stats.length > 0 && (
        <Stack
          direction="row" gap={3} justifyContent="center"
          mt={2.5} sx={{ flexWrap: "wrap" }}
        >
          {stats.map((s) => (
            <Stack key={s.label} alignItems="center" gap={0.3}>
              <Typography sx={{
                fontFamily: FONT_SERIF, fontSize: 16, fontWeight: 400,
                color: INK, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.015em",
                lineHeight: 1,
              }}>
                {s.value}
              </Typography>
              <Typography sx={{
                fontSize: 8.5, fontWeight: 600, color: SLATE,
                letterSpacing: "0.18em", textTransform: "uppercase",
              }}>
                {s.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}
